"use client";

import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { HamburgerIcon, CloseIcon } from "@/components/icons/icons";
import { NAV_ITEMS } from "@/lib/site";
import { useI18n } from "@/i18n/dictionary-context";
import { pathForLocale } from "@/i18n/config";
import { LanguageToggle } from "@/components/sections/language-toggle";

const NAV_LABEL_KEYS: Record<string, keyof ReturnType<typeof useI18n>["dict"]["nav"]> = {
  Platform: "platform",
  "AI Agents": "aiAgents",
  Pricing: "pricing",
  "How it works": "howItWorks",
  FAQ: "faq",
  Blog: "blog",
};

export function MobileNavToggle() {
  const [open, setOpen] = useState(false);
  const { dict, locale } = useI18n();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const drawer = open ? (
    <div className="fixed inset-0 z-50 md:hidden">
      <button
        aria-label={dict.nav.closeMenu}
        onClick={() => setOpen(false)}
        className="absolute inset-0 bg-neutral-950/40 backdrop-blur-sm"
      />
      <div className="absolute right-0 top-0 flex h-full w-[88%] max-w-sm flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-neutral-200 px-6 py-4">
          <span className="text-base font-semibold tracking-tight">
            {dict.nav.menu}
          </span>
          <button
            type="button"
            aria-label={dict.nav.closeMenu}
            onClick={() => setOpen(false)}
            className="inline-flex size-9 items-center justify-center rounded-lg text-neutral-500 hover:bg-neutral-100"
          >
            <CloseIcon className="size-5" />
          </button>
        </div>
        <div className="px-4 pt-4">
          <LanguageToggle full />
        </div>
        <nav className="flex flex-1 flex-col gap-1 px-4 py-4">
          {NAV_ITEMS.map((item) => {
            const labelKey = NAV_LABEL_KEYS[item.label];
            const label = labelKey ? dict.nav[labelKey] : item.label;
            return (
              <Link
                key={item.label}
                href={pathForLocale(item.href, locale)}
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-3 text-base font-medium text-neutral-950 hover:bg-neutral-100"
              >
                {label}
              </Link>
            );
          })}
        </nav>
        <div className="grid gap-2 border-t border-neutral-200 px-6 py-4">
          <Button
            variant="secondary"
            href="https://divetiq-ui-demo.vercel.app/"
            size="md"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
          >
            {dict.nav.login}
          </Button>
          <Button
            variant="primary"
            href={pathForLocale("/contact", locale)}
            size="md"
            onClick={() => setOpen(false)}
          >
            {dict.nav.getDemo}
          </Button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      <button
        type="button"
        aria-label={dict.nav.openMenu}
        aria-expanded={open}
        onClick={() => setOpen(true)}
        className="md:hidden inline-flex size-10 items-center justify-center rounded-lg border border-neutral-200 text-neutral-950 hover:bg-neutral-50"
      >
        <HamburgerIcon className="size-5" />
      </button>

      {drawer ? createPortal(drawer, document.body) : null}
    </>
  );
}
